<script lang="ts" setup>
import { Promotion } from '@rediredi/types'
import { watchDebounced } from '@vueuse/core'

const props = defineProps<{
  promotion?: Promotion
  pictures: string[]
  isOutOfStock: boolean
}>()

const observer = ref()
const refreshKey = ref(0)
const theme = useStoreTheme()
const scrollableContent = ref()
const currentImageIndex = ref(0)
const currentImage = computed(() => {
  return props.pictures[currentImageIndex.value]
})

const viewPicture = (index: number) => {
  const targetImageEl = document.querySelector(
    `.product-picture[data-index="${index}"]`,
  ) as HTMLElement

  if (!targetImageEl) return

  const scrollableContentEl = scrollableContent.value as HTMLElement

  scrollableContentEl?.scroll({
    top: 0,
    left: index * targetImageEl.offsetWidth,
    behavior: 'smooth',
  })
}

const nextPicture = () => {
  const nextPictureIndex =
    currentImageIndex.value + 1 > props.pictures.length - 1 ? 0 : currentImageIndex.value + 1
  viewPicture(nextPictureIndex)
}

const previousPicture = () => {
  viewPicture(
    currentImageIndex.value - 1 < 0 ? props.pictures.length - 1 : currentImageIndex.value - 1,
  )
}

watchDebounced(
  () => props.pictures,
  () => {
    if (observer.value) observer.value.disconnect()
    currentImageIndex.value = 0
    viewPicture(0)

    // Forces the image component to reload so the image can be properly rendered inside its container
    refreshKey.value = refreshKey.value === 0 ? 1 : 0

    const options = {
      root: document.querySelector('#scrollable-area'),
      rootMargin: '0px',
      threshold: 0.6,
    }
    observer.value = new IntersectionObserver((e) => {
      const el = e[0].target as HTMLElement
      const index = el.getAttribute('data-index')

      currentImageIndex.value = Number(index)
    }, options)

    const images = document.querySelectorAll('.product-picture')

    images.forEach((img) => observer.value.observe(img))
  },
  { deep: true, immediate: true, debounce: 150 },
)

onUnmounted(() => observer.value.disconnect())
</script>

<template>
  <div class="product-images relative overflow-hidden rounded-2xl md:w-[452px]">
    <RProductIndicator
      v-if="props.isOutOfStock || props.promotion"
      :type="props.isOutOfStock ? 'outOfStock' : 'promotion'"
      :promotion="props.promotion"
      context="details"
    />

    <div class="relative">
      <button
        v-if="props.pictures.length > 1"
        class="absolute left-2 top-0 flex h-full items-center justify-center text-mercury opacity-90"
        @click="previousPicture"
      >
        <RIcon size="50" name="ic:outline-chevron-left" />
      </button>

      <div
        id="scrollable-area"
        ref="scrollableContent"
        class="no-scrollbar flex aspect-square w-full snap-x snap-mandatory overflow-x-scroll rounded-2xl bg-white shadow-sm md:h-[452px] md:w-[452px]"
      >
        <div
          v-for="(picture, i) in pictures"
          :key="i"
          :data-index="i"
          class="product-picture flex h-auto w-full flex-shrink-0 snap-center items-center justify-center p-4"
        >
          <RProductImage :key="refreshKey" size="medium" class="rounded-2xl" :picture="picture" />
        </div>

        <div
          v-if="!pictures?.length"
          class="product-picture flex h-auto w-full flex-shrink-0 snap-center items-center justify-center p-4"
        >
          <RProductImage :key="refreshKey" size="medium" class="rounded-2xl" :picture="''" />
        </div>
      </div>

      <button
        v-if="props.pictures.length > 1"
        class="absolute right-2 top-0 flex h-full items-center justify-center text-mercury opacity-90"
        @click="nextPicture"
      >
        <RIcon size="50" name="ic:outline-chevron-right" />
      </button>
    </div>

    <div class="mt-4 flex gap-4 overflow-x-auto pb-2" data-testid="product-image-miniatures">
      <button
        v-for="(picture, i) in props.pictures"
        :key="i"
        class="aspect-square h-[72px] w-[72px] flex-shrink-0 flex-grow-0 rounded-2xl border-[1.5px] border-white bg-white p-1 shadow-sm md:h-24 md:w-24"
        :style="{
          borderColor: picture === currentImage ? theme.primary : '',
        }"
        @click="viewPicture(i)"
      >
        <RProductImage
          :key="refreshKey"
          size="thumb"
          class="m-auto rounded-2xl"
          :picture="picture"
        />
      </button>
    </div>
  </div>
</template>
